import React from 'react';
import PropTypes from 'prop-types';
import { TableCell, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Warning } from '@material-ui/icons';
import moment from 'moment';
import * as Status from '../../../constants/status';
import { getArea, destacarVencimento, obterTextoVencimento, formatDate, shouldShowExpirationChip } from '../utils';
import { UIcon } from '../../../components';

const useStyles = makeStyles(theme => ({
  cellContent: {
    fontSize: 12,
  },
  cellLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    marginRight: theme.spacing(0.625),
  },
  cellValue: {
    fontSize: 12,
    marginRight: theme.spacing(0.625),
  },
  iconContainer: {
    display: 'flex',
  },
  icon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main
  },
  chip: {
    marginTop: theme.spacing(0.5),
    fontWeight: 600,
    boxShadow: theme.shadows[1],
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  priorityChip: {
    fontWeight: 600,
    color: '#bd8c00',
    background: 'transparent',
    border: `1px solid #bd8c00`,
  },
  urgencyChip: {
    fontWeight: 600,
    color: theme.palette.error.main,
    background: 'transparent',
    border: `1px solid ${theme.palette.error.main}`,
  },
  adereChip: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    background: 'transparent',
    border: `1px solid ${theme.palette.primary.main}`,
  },
}));

export const EquipamentoNsirCell = React.memo(({ data }) => {
  const classes = useStyles();
  const hasPriority = data.prioridade === 'SIM';
  const hasUrgency = !!data.nsmpUrgencia;
  const showChips = hasPriority || hasUrgency || data.possuiAdere;
  
  return (
    <>
      <span className={classes.cellLabel}>NSIR:</span>
      <span className={classes.cellValue}>{data.codigo}</span>
      {data.equipamento && <>
        <br/>
        <span className={classes.cellLabel}>Equipamento:</span>
        <span className={classes.cellValue}>{data.equipamento}</span>
      </>}
      {showChips && (
        <>
          <br/>
          <div className={classes.chipContainer}>
            {hasPriority && (
              <Chip
                label="Prioridade"
                size="small"
                className={classes.priorityChip}
                aria-label="Prioridade alta"
              />
            )}
            {hasUrgency && (
              <Chip
                label="Urgente"
                size="small"
                className={classes.urgencyChip}
                aria-label="NSMP urgente"
              />
            )}
            {data.possuiAdere && (
              <Chip
                label={"Adere " + data.adereMonth}
                size="small"
                className={classes.adereChip}
                aria-label={data.adereMonth}
              />
            )}
          </div>
        </>
      )}
    </>
  );
});

EquipamentoNsirCell.propTypes = {
  data: PropTypes.shape({
    codigo: PropTypes.string,
    equipamento: PropTypes.string,
    prioridade: PropTypes.string,
    nsmpUrgencia: PropTypes.bool,
  }).isRequired,
};

export const CtPlanExecCell = React.memo(({ data }) => {
  const classes = useStyles();
  const hasPlan = !!data.ctPlan;
  const hasExec = !!data.ctExec;

  return (
    <>
      {hasPlan && (
        <>
          <span className={classes.cellLabel}>Plan:</span>
          <span className={classes.cellValue}>{data.ctPlan}</span>
        </>
      )}
      {hasPlan && hasExec && <br />}
      {hasExec && (
        <>
          <span className={classes.cellLabel}>Exec:</span>
          <span className={classes.cellValue}>{data.ctExec}</span>
        </>
      )}
    </>
  );
});

CtPlanExecCell.propTypes = {
  data: PropTypes.shape({
    ctPlan: PropTypes.string,
    ctExec: PropTypes.string,
  }).isRequired,
};

export const MalhaRegionalAlimentadorCell = React.memo(({ data }) => {
  const classes = useStyles();
  const hasMalha = !!data.malhaCodigo;
  const hasAlimentador = !!data.alimentadorCodigo;

  return (
    <>
      {hasMalha && (
        <>
          <span className={classes.cellLabel}>Malha/Regional:</span>
          <span className={classes.cellValue}>{data.malhaCodigo}</span>
        </>
      )}
      {hasMalha && hasAlimentador && <br />}
      {hasAlimentador && (
        <>
          <span className={classes.cellLabel}>Alimentador:</span>
          <span className={classes.cellValue}>{data.alimentadorCodigo}</span>
        </>
      )}
    </>
  );
});

MalhaRegionalAlimentadorCell.propTypes = {
  data: PropTypes.shape({
    malhaCodigo: PropTypes.string,
    alimentadorCodigo: PropTypes.string,
  }).isRequired,
};

export const TipoServicoCell = React.memo(({ data }) => {
  const classes = useStyles();
  const hasTipoServico = !!data.tipoServicoName;
  const hasSubtipo = !!data.subtipoName;

  return (
    <>
      {hasTipoServico && (
        <span className={classes.cellValue}>{data.tipoServicoName}</span>
      )}
      {hasTipoServico && hasSubtipo && <br />}
      {hasSubtipo && (
        <span className={classes.cellValue}>({data.subtipoName})</span>
      )}
    </>
  );
});

TipoServicoCell.propTypes = {
  data: PropTypes.shape({
    tipoServicoName: PropTypes.string,
    subtipoName: PropTypes.string,
  }).isRequired,
};

export const DatasCell = React.memo(({ data }) => {
  const classes = useStyles();
  const dataVencimento = moment(data.vencimento).endOf('day');
  const showExpirationChip = shouldShowExpirationChip(data);

  return (
    <>
      <span className={classes.cellLabel}>Vencimento:</span>
      <span className={classes.cellValue}>{formatDate(dataVencimento)}</span>
      {showExpirationChip && (
        <>
          <br/> 
          <Chip
            variant="outlined"
            style={destacarVencimento(dataVencimento, moment())}
            size="small"
            label={obterTextoVencimento(dataVencimento, moment())}
            className={classes.chip}
          />
        </>
      )}
      {data.dataConclusaoValue && (
        <>
          <br/> 
          <span className={classes.cellLabel}>Exec. GDIS:</span>
          <span className={classes.cellValue}>{data.dataConclusaoValue}</span>
        </>
      )}
    </>
  );
});

DatasCell.propTypes = {
  data: PropTypes.shape({
    vencimento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dataConclusao: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dataConclusaoValue: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export const PriorityCell = React.memo(({ data }) => {
  const classes = useStyles();
  
  return (
    <div className={classes.iconContainer}>
      {data.prioridade === 'SIM' && <Warning className={classes.icon} aria-label="Prioridade alta" />}
      {data.nsmpUrgencia && <UIcon aria-label="NSMP urgente" />}
    </div>
  );
});

PriorityCell.propTypes = {
  data: PropTypes.shape({
    prioridade: PropTypes.string,
    nsmpUrgencia: PropTypes.bool,
  }).isRequired,
};

export const AreaCell = React.memo(({ data }) => {
  return getArea(data.area);
});

AreaCell.propTypes = {
  data: PropTypes.shape({
    area: PropTypes.string,
  }).isRequired,
};

export const TextCell = React.memo(({ value }) => {
  const classes = useStyles();
  return <span className={classes.cellValue}>{value}</span>;
});

TextCell.propTypes = {
  value: PropTypes.string,
};

TextCell.defaultProps = {
  value: '',
};
